import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DataConcessionScenario } from '../concession-basic-profile.component';
import { ConcessionDataValidationFormUpdateModel } from '../data-concession-validation/data-concession-validation.component';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ConcessionManagementDataValidationForm } from '../../concession-registration.models';
import { ConcessionRegistrationService } from '../../concession-registration.service';
import { RegistrationService } from 'src/app/registration/registration.service';
import { FormGroup } from '@angular/forms';
import { FileData } from 'src/app/constants/app.constants';
import {
  MandatoryDocumentsByQualificationAndCommodity,
  MandatoryDocumentsPerCommodity,
  OptionalDocumentsByCommodity,
  OptionalDocumentsByQualification,
} from 'src/app/interface/config.interface';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FileDeletingEventModel, FileUploadingEventModel } from 'src/app/registration/file/file.models';
import { FileService } from 'src/app/services/file.service';
import { ConcessionDocumentsStateModel } from './concession-files.models';

@Component({
  selector: 'app-concession-files',
  templateUrl: './concession-files.component.html',
  styleUrls: ['./concession-files.component.scss'],
})
export class ConcessionFilesComponent extends DestroyableComponent implements OnInit {
  @Input() dataConcessionScenario: DataConcessionScenario;
  @Input() technicianId: string | number;
  @Input() technicianApplication: any;
  @Input() autosave: boolean;
  @Output() stateChange = new EventEmitter<ConcessionDocumentsStateModel>();

  documents: any = [];
  documentsPristine: any;
  documentsIdsToDelete: number[] = [];
  fileUploadEvents: FileUploadingEventModel[] = [];

  concessionManagementDataValidationForm: ConcessionManagementDataValidationForm;
  specialistMandatoryDocumentsTypes: Set<string> = new Set<string>();
  takeMandatoryDocumentsTypes: Set<string> = new Set<string>();
  optionalDocumentsTypes: Set<string> = new Set<string>();
  optionalDocumentsByCommodity: OptionalDocumentsByCommodity[] = this.environmentService.optionalDocumentsByCommodity || [];
  mandatoryDocumentsPerCommodity: MandatoryDocumentsPerCommodity = this.environmentService.mandatoryDocumentsPerCommodity;
  showSafetyTechnologyExemptionDocument: boolean = this.environmentService.showSafetyTechnologyExemptionDocument;
  showSafetyTechnologyAuthorizationDocument: boolean = this.environmentService.showSafetyTechnologyAuthorizationDocument;
  optionalDocumentsByQualification: OptionalDocumentsByQualification[] = this.environmentService.optionalDocumentsByQualification;
  mandatoryDocumentsByQualificationAndCommodity: MandatoryDocumentsByQualificationAndCommodity[] =
    this.environmentService.mandatoryDocumentsByQualificationAndCommodity || [];
  allDocumentsResultingOfEmptyChecks: boolean;
  commodities = ['power', 'gas', 'water', 'heating'];

  technicianDataFG: FormGroup;
  checkDataFG: FormGroup;
  companyDataFG: FormGroup;

  negativeIncrement = 0;
  isDirty: boolean;

  getNegativeIncrement(): number {
    return --this.negativeIncrement;
  }

  constructor(
    private concessionRegistrationService: ConcessionRegistrationService,
    private registrationService: RegistrationService,
    private environmentService: EnvironmentService,
    private fileService: FileService
  ) {
    super();
  }

  ngOnInit(): void {
    this.emitStateChange();
    this.technicianDataFG = this.registrationService.technicianDataFG;
    this.checkDataFG = this.registrationService.checkDataFG;
    this.companyDataFG = this.registrationService.companyDataFG;
    this.preventLeak(this.concessionRegistrationService.concessionDataValidationFormUpdated$).subscribe(
      (data: ConcessionDataValidationFormUpdateModel) => {
        this.concessionManagementDataValidationForm = data.formValue;
        this.commodities.forEach(c => this.setMandatoryDocuments(c, this.checkDataFG.value));
        this.emitStateChange();
      }
    );

    const checkData = this.checkDataFG.value;
    this.setRequiredDocuments(checkData);
    this.addMandatoryDocumentsByQualification(!checkData.power && !checkData.gas && !checkData.water && !checkData.heating);
    this.setOptionalDocuments();
    this.updateOptionalDocumentsByQualification();
    this.commodities.forEach(c => this.setMandatoryDocuments(c, checkData));
    this.parseDocumentValues();
  }

  setMandatoryDocuments(commodityType: string, checkData: any): void {
    if (!commodityType) {
      return;
    }

    switch (commodityType) {
      case 'power':
        if (checkData[commodityType]) {
          this.updateRequiredDocumentsAfterEmptyChecks();
          this.mandatoryDocumentsPerCommodity.power.documentsCompany.forEach(d => this.takeMandatoryDocumentsTypes.add(d));
          this.mandatoryDocumentsPerCommodity.power.documentsSpecialist.forEach(d => this.specialistMandatoryDocumentsTypes.add(d));
        } else {
          this.setRequiredDocuments(checkData);
        }
        break;
      case 'gas':
        if (checkData[commodityType]) {
          this.updateRequiredDocumentsAfterEmptyChecks();
          this.mandatoryDocumentsPerCommodity.gas.documentsCompany.forEach(d => this.takeMandatoryDocumentsTypes.add(d));
          this.mandatoryDocumentsPerCommodity.gas.documentsSpecialist.forEach(d => this.specialistMandatoryDocumentsTypes.add(d));
        } else {
          this.setRequiredDocuments(checkData);
        }
        break;
      case 'water':
        if (checkData[commodityType]) {
          this.updateRequiredDocumentsAfterEmptyChecks();
          this.mandatoryDocumentsPerCommodity.water.documentsCompany.forEach(d => this.takeMandatoryDocumentsTypes.add(d));
          this.mandatoryDocumentsPerCommodity.water.documentsSpecialist.forEach(d => this.specialistMandatoryDocumentsTypes.add(d));
        } else {
          this.setRequiredDocuments(checkData);
        }
        break;
      case 'heating':
        if (checkData[commodityType]) {
          this.updateRequiredDocumentsAfterEmptyChecks();
          this.mandatoryDocumentsPerCommodity.districtHeating.documentsCompany.forEach(d => this.takeMandatoryDocumentsTypes.add(d));
          this.mandatoryDocumentsPerCommodity.districtHeating.documentsSpecialist.forEach(d =>
            this.specialistMandatoryDocumentsTypes.add(d)
          );
        } else {
          this.setRequiredDocuments(checkData);
        }
        break;
    }

    if (this.concessionManagementDataValidationForm.hasWeldingLicense) {
      this.specialistMandatoryDocumentsTypes.add('welding_certificate_confirmation');
    }

    this.removeSpecialistMandatoryDocuments();
  }

  removeSpecialistMandatoryDocuments(): void {
    const technicianData = this.technicianDataFG.value;
    switch (technicianData?.qualification_type) {
      case 'Meister':
        this.optionalDocumentsTypes.add(FileData.PROOFOFCONTRACT);
        this.optionalDocumentsTypes.add(FileData.PROOFOFEXPERTISE);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFQUALIFICATION);
        break;
      case 'Ingenieur':
        this.specialistMandatoryDocumentsTypes.delete(FileData.GASCOURSECENTRALHEATINGVENTILATIONDURATION);
        this.optionalDocumentsTypes.add(FileData.PROOFOFCONTRACT);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFEXPERTISE);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFQUALIFICATION);
        break;
      case 'Meister In anderem Handwerk':
        this.specialistMandatoryDocumentsTypes.delete(FileData.GASCOURSECENTRALHEATINGVENTILATIONDURATION);
        this.optionalDocumentsTypes.add(FileData.PROOFOFCONTRACT);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFEXPERTISE);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFQUALIFICATION);
        break;
      case 'Ausnahmebewilligung':
        this.specialistMandatoryDocumentsTypes.delete(FileData.GASCOURSECENTRALHEATINGVENTILATIONDURATION);
        this.optionalDocumentsTypes.add(FileData.PROOFOFQUALIFICATION);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFEXPERTISE);
        this.optionalDocumentsTypes.delete(FileData.PROOFOFCONTRACT);
        break;
    }
  }
  private updateOptionalDocumentsByQualification() {
    const technicianData = this.technicianDataFG.value;

    const qualificationTypes = ['Meister', 'Ingenieur', 'Meister In anderem Handwerk', 'Ausnahmebewilligung', 'Altgeselle'];

    if (technicianData?.qualification_type && qualificationTypes.includes(technicianData.qualification_type)) {
      this.optionalDocumentsByQualification
        ?.find(docs => docs.qualification === technicianData.qualification_type)
        ?.documents.forEach(doc => this.optionalDocumentsTypes.add(doc));
    }
  }
  private setRequiredDocuments(checkData: any) {
    if (!checkData.power && !checkData.gas && !checkData.water && !checkData.heating) {
      this.setRequiredDocumentsOnEmptyChecks();
    } else {
      this.takeMandatoryDocumentsTypes = new Set([
        ...((checkData.power && this.mandatoryDocumentsPerCommodity.power.documentsCompany) || []),
        ...((checkData.gas && this.mandatoryDocumentsPerCommodity.gas.documentsCompany) || []),
        ...((checkData.water && this.mandatoryDocumentsPerCommodity.water.documentsCompany) || []),
        ...((checkData.heating && this.mandatoryDocumentsPerCommodity.districtHeating.documentsCompany) || []),
      ]);

      this.specialistMandatoryDocumentsTypes = new Set([
        ...((checkData.power && this.mandatoryDocumentsPerCommodity.power.documentsSpecialist) || []),
        ...((checkData.gas && this.mandatoryDocumentsPerCommodity.gas.documentsSpecialist) || []),
        ...((checkData.water && this.mandatoryDocumentsPerCommodity.water.documentsSpecialist) || []),
        ...((checkData.heating && this.mandatoryDocumentsPerCommodity.districtHeating.documentsSpecialist) || []),
      ]);
    }
  }

  private setOptionalDocuments(): void {
    const checkData = this.checkDataFG.value;
    this.optionalDocumentsTypes.clear();
    if (!checkData.power && !checkData.gas && !checkData.water && !checkData.heating) {
      this.setOptionalDocumentsByCommodityOnEmptyChecks();
      this.addMandatoryDocumentsByQualification(true);
    } else {
      if (this.optionalDocumentsByCommodity) {
        if (checkData.gas) {
          this.optionalDocumentsByCommodity
            .find(docs => docs.commodity === 'gas')
            ?.documents.forEach(doc => this.optionalDocumentsTypes.add(doc));
        }

        if (checkData.water) {
          this.optionalDocumentsByCommodity
            .find(docs => docs.commodity === 'water')
            ?.documents.forEach(doc => this.optionalDocumentsTypes.add(doc));
        }

        if (checkData.power) {
          this.optionalDocumentsByCommodity
            .find(docs => docs.commodity === 'power')
            ?.documents.forEach(doc => this.optionalDocumentsTypes.add(doc));
        }

        if (checkData.heating) {
          this.optionalDocumentsByCommodity
            .find(docs => docs.commodity === 'districtHeating')
            ?.documents.forEach(doc => this.optionalDocumentsTypes.add(doc));
        }
      }
    }
  }

  addMandatoryDocumentsByQualification(shouldAddForAllCommodities?: boolean): void {
    const technicianData = this.technicianDataFG.value;
    if (shouldAddForAllCommodities) {
      this.mandatoryDocumentsByQualificationAndCommodity
        ?.find(docs => docs.qualification === technicianData.qualification_type)
        ?.documents.forEach(d => this.specialistMandatoryDocumentsTypes.add(d));
    } else {
      this.mandatoryDocumentsByQualificationAndCommodity
        ?.find(
          docs =>
            docs.qualification === technicianData.qualification_type &&
            docs.commodities.some(
              commodity => this.checkDataFG.value.hasOwnProperty(commodity) && this.checkDataFG.value[commodity] === true
            )
        )
        ?.documents.forEach(doc => this.specialistMandatoryDocumentsTypes.add(doc));
    }
  }

  fileEvent(): void {
    // this.registrationService.getTechnicianApplication(this.technicianId, 'technician_id').subscribe((response: any) => {
    //   this.technicianApplication.document = response.technician_application[0].document;
    //   this.parseDocumentValues();
    // });
  }

  uploadFile(event: FileUploadingEventModel): void {
    const documentToAdd = {
      application_id: '',
      converter_id: '',
      document_id: this.getNegativeIncrement(),
      file: null,
      filename: event.event?.currentFiles[0]?.name,
      filesize: this.parseFileSize(event.event.currentFiles[0]?.size || 0),
      generator_id: '',
      integrated_protection_id: '',
      inverter_id: '',
      name: event.name,
      photovoltaic_id: '',
      power_plant_protection_id: '',
      sk_document_type: null,
      ta_id: event.taId,
    };

    this.documents?.push(documentToAdd);
    this.fileUploadEvents.push(event);
    this.emitStateChange(true);
  }

  deleteFile(event: FileDeletingEventModel): void {
    this.technicianApplication.document = this.technicianApplication.document?.filter((d: { document_id: string }) => {
      return d.document_id !== event.id;
    });
    this.documents = this.technicianApplication.document;

    const documentId = parseInt(event.id, 0);
    if (documentId > 0) {
      this.documentsIdsToDelete = [...this.documentsIdsToDelete, documentId];
    }
    this.emitStateChange(true);
  }

  saveChanges(): void {
    this.documentsIdsToDelete.forEach(async id => {
      await this.fileService.removeFile(id.toString()).toPromise();
    });

    this.fileUploadEvents.forEach(async e => {
      await this.fileService.uploadDocuments(e.event, e.taId, e.name).toPromise();
    });

    this.documentsIdsToDelete = [];
    this.fileUploadEvents = [];
    this.emitStateChange(false);
  }

  resetChanges() {
    this.documentsIdsToDelete = [];
    this.fileUploadEvents = [];
    this.documents = JSON.parse(JSON.stringify(this.documentsPristine));
    this.technicianApplication.document = this.documents;

    this.emitStateChange(false);
  }

  private parseDocumentValues(): void {
    if (this.technicianApplication && this.technicianApplication.document) {
      this.documents = this.technicianApplication.document.map(data => {
        data.filesize = this.parseFileSize(data.filesize);
        return data;
      });
      this.documentsPristine = JSON.parse(JSON.stringify(this.documents));
    }
  }

  private parseFileSize(fileSize: number): string {
    if (fileSize.toString().length > 3) {
      return (fileSize / 1024).toFixed(2) + ' kb';
    } else {
      return fileSize + ' bytes';
    }
  }

  allTakeMandatoryDocumentsUploaded(): boolean {
    return (
      [...this.takeMandatoryDocumentsTypes].every(mandatoryDoc => this.documents.some(doc => doc.name === mandatoryDoc)) &&
      [...this.specialistMandatoryDocumentsTypes]
        .filter(e => !this.optionalDocumentsTypes.has(e))
        .every(mandatoryDoc => this.documents.some(doc => doc.name === mandatoryDoc))
    );
  }

  private emitStateChange(dirty?: boolean) {
    this.isDirty = dirty ?? this.isDirty;

    this.stateChange.emit({ dirty: this.isDirty, isValid: this.allTakeMandatoryDocumentsUploaded() });
  }

  private setOptionalDocumentsByCommodityOnEmptyChecks(): void {
    this.optionalDocumentsByCommodity.forEach(doc => doc.documents.forEach(d => this.optionalDocumentsTypes.add(d)));
  }

  private setRequiredDocumentsOnEmptyChecks() {
    this.allDocumentsResultingOfEmptyChecks = true;
    this.takeMandatoryDocumentsTypes = new Set([
      ...this.mandatoryDocumentsPerCommodity.power.documentsCompany,
      ...this.mandatoryDocumentsPerCommodity.gas.documentsCompany,
      ...this.mandatoryDocumentsPerCommodity.water.documentsCompany,
      ...this.mandatoryDocumentsPerCommodity.districtHeating.documentsCompany,
    ]);

    this.specialistMandatoryDocumentsTypes = new Set([
      ...this.mandatoryDocumentsPerCommodity.power.documentsSpecialist,
      ...this.mandatoryDocumentsPerCommodity.gas.documentsSpecialist,
      ...this.mandatoryDocumentsPerCommodity.water.documentsSpecialist,
      ...this.mandatoryDocumentsPerCommodity.districtHeating.documentsSpecialist,
    ]);
  }

  private updateRequiredDocumentsAfterEmptyChecks() {
    if (this.allDocumentsResultingOfEmptyChecks) {
      this.takeMandatoryDocumentsTypes = new Set<string>();
      this.specialistMandatoryDocumentsTypes = new Set<string>();

      this.allDocumentsResultingOfEmptyChecks = false;
    }
  }
}
